import type { ComponentType } from 'react';
import type { Permission } from '@/config/permissions';
import type { ModuleId } from '@/types';
import { adminPaths, modulePaths, routePaths } from '@/routes/paths';
import {
  IconAdmin,
  IconBilling,
  IconCalendar,
  IconCategory,
  IconCommercial,
  IconDashboard,
  IconFields,
  IconInventory,
  IconBell,
  IconLogistics,
  IconMaintenance,
  IconReservations,
  IconUsers,
} from '@/shared/components/icons';

export type SidebarItem = {
  id: string;
  label: string;
  path: string;
  end?: boolean;
  icon: ComponentType<{ size?: number }>;
  permission?: Permission;
  moduleId?: ModuleId;
  children?: readonly SidebarItem[];
};

/** Navegación principal del ERP (sidebar). */
export const SIDEBAR_NAV: readonly SidebarItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: routePaths.home,
    end: true,
    icon: IconDashboard,
    moduleId: 'dashboard',
  },
  {
    id: 'notifications',
    label: 'Notificaciones',
    path: routePaths.notifications,
    icon: IconBell,
    moduleId: 'dashboard',
  },
  {
    id: 'inventory',
    label: 'Inventario',
    path: modulePaths.inventory,
    icon: IconInventory,
    moduleId: 'inventory',
  },
  {
    id: 'inventory-movements',
    label: 'Movimientos',
    path: `${modulePaths.inventory}/movimientos`,
    icon: IconInventory,
    moduleId: 'inventory',
    permission: 'inventory.movements.read',
  },
  {
    id: 'inventory-locations',
    label: 'Ubicaciones',
    path: `${modulePaths.inventory}/ubicaciones`,
    icon: IconInventory,
    moduleId: 'inventory',
    permission: 'inventory.movements.read',
  },
  {
    id: 'categories',
    label: 'Categorías',
    path: modulePaths.categories,
    icon: IconCategory,
    moduleId: 'inventory',
  },
  {
    id: 'subcategories',
    label: 'Subcategorías',
    path: `${modulePaths.inventory}/subcategorias`,
    icon: IconCategory,
    moduleId: 'inventory',
    permission: 'catalog.write',
  },
  {
    id: 'custom-fields',
    label: 'Campos personalizados',
    path: modulePaths.customFields,
    icon: IconFields,
    moduleId: 'inventory',
  },
  {
    id: 'commercial',
    label: 'Comercial',
    path: modulePaths.commercial,
    icon: IconCommercial,
    moduleId: 'commercial',
    children: [
      {
        id: 'commercial-clients',
        label: 'Clientes',
        path: `${modulePaths.commercial}/clientes`,
        icon: IconCommercial,
        moduleId: 'commercial',
      },
      {
        id: 'commercial-operations',
        label: 'Operaciones',
        path: `${modulePaths.commercial}/operaciones`,
        icon: IconCommercial,
        moduleId: 'commercial',
        end: true,
      },
    ],
  },
  {
    id: 'billing',
    label: 'Facturación',
    path: modulePaths.billing,
    icon: IconBilling,
    moduleId: 'billing',
    children: [
      {
        id: 'billing-invoices',
        label: 'Facturas',
        path: `${modulePaths.billing}/facturas`,
        icon: IconBilling,
        moduleId: 'billing',
        end: true,
      },
    ],
  },
  {
    id: 'reservations',
    label: 'Reservas',
    path: modulePaths.reservations,
    icon: IconReservations,
    moduleId: 'reservations',
  },
  {
    id: 'maintenance',
    label: 'Mantenimiento',
    path: modulePaths.maintenance,
    icon: IconMaintenance,
    moduleId: 'maintenance',
    permission: 'maintenance.read',
  },
  {
    id: 'calendar',
    label: 'Agenda',
    path: modulePaths.calendar,
    icon: IconCalendar,
    moduleId: 'calendar',
  },
  {
    id: 'logistics',
    label: 'Logística',
    path: modulePaths.logistics,
    icon: IconLogistics,
    moduleId: 'logistics',
    children: [
      {
        id: 'logistics-orders',
        label: 'Órdenes',
        path: modulePaths.logistics,
        icon: IconLogistics,
        moduleId: 'logistics',
        end: true,
      },
      {
        id: 'logistics-providers',
        label: 'Proveedores',
        path: `${modulePaths.logistics}/proveedores`,
        icon: IconLogistics,
        moduleId: 'logistics',
      },
    ],
  },
  {
    id: 'admin',
    label: 'Administración',
    path: modulePaths.admin,
    icon: IconAdmin,
    moduleId: 'admin',
    children: [
      {
        id: 'admin-users',
        label: 'Usuarios',
        path: `${modulePaths.admin}/${adminPaths.users}`,
        icon: IconUsers,
        permission: 'users.manage',
      },
      {
        id: 'admin-audit',
        label: 'Auditoría',
        path: `${modulePaths.admin}/${adminPaths.audit}`,
        icon: IconAdmin,
        permission: 'audit.read',
      },
    ],
  },
] as const;
